import { Fragment, useEffect, useState, useRef, useContext } from "react";
import {
  Button,
  Checkbox,
  Typography,
  message,
  Row,
  Col,
  Divider,
  Skeleton,
  Input,
  Form,
  Space,
  Tag,
  Collapse,
  List,
  Card,
  Affix,
  Steps,
  Alert,
  Modal,
} from "antd";
import moment from "moment";
import "./PQQ.css";
import {
  getVendorProfile,
  AttachVendorProfile,
  UpdateVendorApplication,
  recertificationAttest,
} from "../../services/applications.services";
import ResultWizz from "../../blocks/ResultWizz/ResultWizz";
import { useHistory } from "react-router";
import PQQDescriptions from "./PQQDescriptions";
import AlertWizz from "../../blocks/AlertWizz/AlertWizz";
import {
  adminApplicationPriorityOptions,
  applicationStatusValues,
  USER,
} from "../../constants/defaultKeys";
import { FilePdfOutlined, CheckCircleOutlined, DollarOutlined, FileTextOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { isUserClientOrProjectAdmin } from "../../helpers/utility";
import { getItem } from "../../helpers/localStorage";
import { getPaymentURL } from "../../services/payment.services";
import { SelectedVendorDetailsContext } from "../../context/SelectedVendorContext";
import { getVendorDetails } from "../../services/vendorProfile.services";

const { Paragraph, Text } = Typography;
const { Panel } = Collapse;

const PQQContent = (props) => {
  const {
    application,
    vendorId,
    vendorDetails,
    refresh,
    isUserAdmin,
    setShowDrawer,
    openPQQForm,
  } = props;

  const history = useHistory();
  const [form] = Form.useForm();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const userDetails = JSON.parse(getItem(USER));
  const certificationRef = useRef(null);
  const vendorContext = useContext(SelectedVendorDetailsContext);

  // Recertification gate (Phase 1) state
  // Prefer recertify from the application API; vendor context can be stale after checkout.
  const recertifyFlag =
    typeof application?.recertify === "boolean"
      ? application.recertify
      : Boolean(vendorDetails?.recertify);
  const isRecertify =
    application?.status === "Recertify" || recertifyFlag;
  const isRecertifyAttested = !!application?.recertification_attested_at;
  const reviewedKey = application?.application_id
    ? `i4s.recertReviewed.${application.application_id}`
    : null;
  const [hasReviewedProfile, setHasReviewedProfile] = useState(() => {
    if (!reviewedKey) return false;
    try {
      return window.localStorage.getItem(reviewedKey) === "true";
    } catch (e) {
      return false;
    }
  });
  const [attestModalOpen, setAttestModalOpen] = useState(false);
  const [attestChecked, setAttestChecked] = useState(false);
  const [attesting, setAttesting] = useState(false);

  useEffect(() => {
    // If the cycle attestation is already recorded, surface that state
    // visually (the "Reviewed" step is implied by attestation).
    if (isRecertifyAttested) {
      setHasReviewedProfile(true);
    }
  }, [isRecertifyAttested]);

  const markProfileReviewed = () => {
    setHasReviewedProfile(true);
    if (reviewedKey) {
      try {
        window.localStorage.setItem(reviewedKey, "true");
      } catch (e) {
        // localStorage unavailable; the in-memory flag is still set
      }
    }
  };

  const submitAttestation = async () => {
    if (!application?.application_id) return;
    try {
      setAttesting(true);
      await recertificationAttest(application.application_id);
      message.success(
        "Thanks for confirming. You can now reattach your i4Profile and pay."
      );
      setAttestModalOpen(false);
      setAttestChecked(false);
      refresh();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.message ||
        "Failed to record confirmation. Please try again.";
      message.error(msg);
    } finally {
      setAttesting(false);
    }
  };

  useEffect(() => {
    if (!application?.application_id) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    getVendorProfile(application?.application_id)
      .then((res) => {
        setData(res.data?.data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("error getting e vendor profile", err);
        setIsLoading(false);
        if (err && err.response) {
          message.error(err.response.data.message || err.response.message || "Failed to load vendor profile");
        }
      });
  }, [application]);

  // Scroll to top when application is successfully submitted
  useEffect(() => {
    if (application?.is_approved_and_submitted && application?.status === applicationStatusValues[1].value) {
      // Small delay to ensure the success message is rendered
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }, [application?.is_approved_and_submitted, application?.status]);

  const attachApp = () => {
    setIsLoading(true);
    AttachVendorProfile(vendorId, application.id)
      .then((response) => {
        setData(response.data.data);
        setIsLoading(false);
        refresh();
      })
      .catch((error) => {
        console.error("attach vendor profile error", error);
        setIsLoading(false);
        if (error && error.response) {
          message.error(error.response.data.message || error.response.message);
        } else {
          message.error("attach vendor profile error");
        }
      });
  };

  const updateAttachApp = (values) => {
    const formData = {
      vendor_id: vendorId,
      application_id: application.id,
      bid_nos: application.bid_no,
      approve_and_submit: values.approve_and_submit,
      submitted_by: values.submitted_by,
    };
    setIsLoading(true);
    if (vendorDetails?.is_approver) {
      UpdateVendorApplication(formData)
        .then((response) => {
          refresh();
          if (vendorContext?.setVendorDetails && vendorId) {
            getVendorDetails(vendorId)
              .then((res) => {
                vendorContext.setVendorDetails({ ...res.data.data });
              })
              .catch(() => {
                /* refresh() already refetched PQQ; banner clears on next navigation */
              });
          }
          // Scroll to top after successful certification
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 300);
        })
        .catch((error) => {
          message.error(error.response.data.message);
          console.error(error);
        })
        .finally(() => setIsLoading(false));
    } else {
      message.error("You're not authorized to attach profile");
    }
  };

  async function handlePayment() {
    try {
      setIsLoading(true);
      const res = await getPaymentURL({
        vendor_id: vendorId,
        pqq: application?.application_id,
      });
      window.location.href = res.data.url;
    } catch (error) {
      console.error("error", error);
      if (error && error.response) {
        message.error(error.response.data.message || error.response.message);
      } else {
        message.error("get payment URL error");
      }
    } finally {
      setIsLoading(false);
    }
  }

  function appStatusProps() {
    const renewalActive =
      recertifyFlag || application?.status === "Recertify";

    // DB may still show Approved from a prior cycle while vendor.recertify is true,
    // or status is Recertify — never show "your PQQ is approved" during an open renewal.
    if (renewalActive) {
      const needsRenewalPayment = isRecertify || !application?.is_paid;
      if (needsRenewalPayment) {
        return {
          status: "warning",
          title: "Annual recertification — action required",
          subTitle:
            "Your previous approval was for an earlier cycle. Reattach your i4Profile if you made updates, pay the renewal fee ($750.00 + 2.9% transaction fee), then certify and submit this PQQ below.",
        };
      }
      if (!application?.is_approved_and_submitted) {
        return {
          status: "info",
          title: "Renewal payment received",
          subTitle:
            "Review your details below, then complete certification and submit your PQQ for this renewal cycle.",
        };
      }
      // Still renewing while DB shows paid + submitted from a prior cycle (e.g. subscription
      // job didn't clear flags). Never fall through to "Congratulations" while vendor.recertify is true.
      return {
        status: "warning",
        title: "Annual recertification — action required",
        subTitle:
          "Complete this renewal cycle: reattach your i4Profile if you updated it, pay the renewal fee ($750.00 + 2.9% transaction fee) if you have not for this year, then certify and submit below.",
      };
    }

    switch (application.status) {
      case applicationStatusValues[0].value:
        // If payment is complete, show payment success message
        if (application?.is_paid) {
          return {
            status: "success",
            title: "Payment Successful!",
            subTitle:
              "Great! Your payment has been processed successfully. You're now ready to certify and submit your PQQ. Review the details below, then scroll down to complete the certification. Need to make changes? Update your profile and reattach it.",
          };
        }
        // Otherwise, show profile attached message
        return {
          status: "info",
          title: "Profile Attached Successfully!",
          subTitle:
            "Great! Your i4Profile has been attached to this application. Review the details below, then proceed to payment and certification. Need to make changes? Update your profile and reattach it.",
        };
      case applicationStatusValues[1].value:
        return {
          status: "success",
          title: "Thanks for submitting application",
          subTitle:
            "We may require additional information as part of the i4 verification process. We'll start reviewing your PQQ submission and send notifications to your email if any additional information is required. If you like to change your i4Profile and resubmit your application, you can do it using the button below the PQQ details.",
        };
      case applicationStatusValues[2].value:
      case applicationStatusValues[3].value:
        return {
          status: "success",
          title: "Your application is under review",
          subTitle:
            "We've started reviewing your application and will reach out to you if any additional information is required. Please note that you won't be able to make any edits to your PQQ application.",
        };
      case applicationStatusValues[4].value:
        return {
          status: "warning",
          title: "Additional Information Required",
          subTitle:
            "Please review comments on the PQQ application and edit your i4Profile. Once you change your i4Profile as advised on comments, please reattach your application. Please reach out to us for any questions.",
        };
      case applicationStatusValues[5].value:
        // Admin has approved in the past; vendor may still owe pay/certify this cycle.
        if (!application?.is_paid || !application?.is_approved_and_submitted) {
          if (!application?.is_paid) {
            return {
              status: "warning",
              title: "Payment required",
              subTitle:
                "Complete your subscription payment below, then certify and submit your PQQ.",
            };
          }
          return {
            status: "info",
            title: "Certify and submit your PQQ",
            subTitle:
              "Review the summary below and complete certification to submit for review.",
          };
        }
        return {
          status: "success",
          title: "Congratulations! your PQQ is approved",
          subTitle:
            "We're glad to let you know your PQQ has been approved. We have forwarded the client about your application status. Thanks for providing us the opportunity to verify your profile",
        };
      case applicationStatusValues[6].value:
        return {
          status: "error",
          title: "Sorry! your PQQ is rejected",
          subTitle:
            "We're sorry to let you know your PQQ has been rejected. Your application did not meet the eligibility criteria set by the client. Thanks for providing us the opportunity to verify your profile",
        };
      // NEED TO CHANGE COMPLETE STATUS MESSAGE AND ACTIONS
      case applicationStatusValues[7].value:
        return {
          completed: true,
          status: "success",
          title: "Your PQQ is Completed",
          subTitle:
            "This is to inform you that your PQQ is Completed. Thanks for providing us the opportunity to verify your profile ",
          // "We're glad to let you know your PQQ has been Completed. We have forwarded the client about your application status. Thanks for providing us the opportunity to verify your profile",
        };
      default:
        return "";
    }
  }

  function resultProps() {
    switch (isUserAdmin) {
      case true:
        return application.status === applicationStatusValues[0].value
          ? {
              title: "Vendor has not attached i4Profile to the PQQ yet",
              subTitle:
                "You may please review his i4Profile by using the button above.",
            }
          : {
              title: "Something wrong with loading the details",
              subTitle: "Please contact web development team for help",
            };
      default:
        return application?.length === 0
          ? {
              completed: true,

              status: "success",
              title: "Congratulations on completing your i4 profile!",
              subTitle:
                "Please create a new PQQ below. Once created you will be able to certify and submit for review",
              extra: (
                <>
                  <Button
                    size="large"
                    type="primary"
                    onClick={openPQQForm}
                    disabled={
                      !(
                        vendorDetails?.is_subscribed ||
                        vendorDetails?.profile_completion_status == 100
                      )
                    }
                  >
                    Create PQQ
                  </Button>
                </>
              ),
              // title: "You don't have any PQQ's",
              // subTitle: "Please create a PQQ to see the PQQ details",
            }
          : vendorDetails?.profile_completion_status !== 100
          ? {
              title: "Please complete your i4 Profile to apply",
              subTitle:
                "Click the below button to complete filling your i4 Profile",
              extra: (
                <Button
                  type="primary"
                  onClick={() =>
                    history.push(
                      `/vendor/${vendorId}/vendor-profile/basic-details`
                    )
                  }
                >
                  Click Here!
                </Button>
              ),
            }
          : !vendorDetails?.is_approver
          ? {
              title: "You need to be an approver to apply",
              subTitle: "Please contact i4Strategies admin for approver role",
            }
          : application?.status === applicationStatusValues[0].value ||
            application?.status === "Recertify"
          ? {
              title:
                application?.status === "Recertify"
                  ? "Recertification Required"
                  : "Apply with Your i4Profile",
              subTitle:
                application?.status === "Recertify"
                  ? "Please attach your latest i4Profile to continue with payment and recertification."
                  : "Your profile is complete! Attach it to your application to continue with payment and certification.",
              extra: (
                <Button type="primary" onClick={attachApp}>
                  Attach i4Profile
                </Button>
              ),
            }
          : application?.status === applicationStatusValues[7].value
          ? {
              title: "Your application review is completed ",
            }
          : {
              title: "Something went wrong",
              subTitle:
                "Not able to fetch your application details. Please reach out to support for help",
              extra: <Button type="primary">Contact us</Button>,
            };
    }
  }

  function discardAndReattach() {
    switch (application.status) {
      case applicationStatusValues[0].value:
      case applicationStatusValues[1].value:
      case applicationStatusValues[4].value:
        return (
          <Button danger style={{ marginTop: "20px" }} onClick={attachApp}>
            Discard and reattach latest i4Profile
          </Button>
        );
      default:
        return true;
    }
  }

  const scrollToCertification = () => {
    certificationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  function getProgressSteps() {
    const profileAttached = !!data || 
                            (application?.profile_snapshot !== null && 
                             application?.profile_snapshot !== undefined &&
                             application?.profile_snapshot !== false);
    // During recertification, prior-cycle is_paid / approve flags can stay true; progress should
    // reflect the current cycle (payment counts after vendor.recertify clears at checkout success).
    const isPaid = application?.is_paid && !isRecertify;
    const isSubmitted =
      application?.is_approved_and_submitted && !isRecertify;
    
    const steps = [
      {
        title: 'Profile Attached',
        status: profileAttached ? 'finish' : 'wait',
        // Explicitly show green checkmark for finished steps
        icon: profileAttached ? (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
        ) : (
          <FileTextOutlined />
        ),
      },
      {
        title: 'Payment',
        status: isPaid ? 'finish' : (profileAttached ? 'process' : 'wait'),
        icon: isPaid ? (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
        ) : (
          <CheckCircleOutlined />
        ),
      },
      {
        title: 'Certify & Submit',
        status: isSubmitted ? 'finish' : (isPaid ? 'process' : 'wait'),
        icon: isSubmitted ? (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
        ) : (
          <CheckCircleOutlined />
        ),
      },
    ];
    return steps;
  }

  function certifyBlock() {
    const needsRenewalPayment =
      isRecertify || !application?.is_paid;

    if (!isRecertify && application?.is_approved_and_submitted) {
      return true;
    }

    if (needsRenewalPayment) {
      const payLabel = isRecertify ? "$750.00" : "$1,250.00";
      return (
        <div ref={certificationRef}>
          <Card
            title={
              <Space>
                <DollarOutlined style={{ color: '#1890ff' }} />
                <Text strong>Payment Required</Text>
              </Space>
            }
            style={{ marginTop: '24px', border: '2px solid #1890ff' }}
          >
            <Alert
              message="Complete Payment to Continue"
              description="Before you can certify and submit your PQQ, you must complete payment. Pre-Qualification Questionnaires will remain pending until payment is received and processed."
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />
            <Paragraph>
              Once your payment is confirmed, you will be able to certify and submit your PQQ.
            </Paragraph>
            <Button type="primary" size="large" onClick={handlePayment} block>
              Pay Now - {payLabel} + 2.9% transaction fee
            </Button>
          </Card>
        </div>
      );
    }

    return (
          <div ref={certificationRef}>
            <Card
              title={
                <Space>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <Text strong>Certify and Submit Your PQQ</Text>
                </Space>
              }
              style={{ marginTop: '24px', border: '2px solid #52c41a' }}
            >
              <Alert
                message="Final Step: Certification Required"
                description="Please review the information above and certify that all information is accurate and complete."
                type="success"
                showIcon
                style={{ marginBottom: '16px' }}
              />
              <Paragraph>
                By checking the box below, I hereby certify that I am an
                authorized representative of the above-name experience the best of
                my knowledge, the information contained in the above completed
                pre-qualification questionnaire submitted on the date specified
                above (or submitted separately) is full, complete and accurate;
                and to the best of my knowledge, that information continues to be
                full, complete and accurate.
              </Paragraph>
              <Divider />
              <Form
                name="pqqSubmitForm"
                colon={false}
                form={form}
                onFinish={updateAttachApp}
                scrollToFirstError
              >
                <Form.Item
                  name="approve_and_submit"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                "Please check this box to continue to submit"
                              )
                            ),
                    },
                  ]}
                >
                  <Checkbox disabled={!vendorDetails?.is_approver} style={{ fontSize: '16px' }}>
                    <Text strong>I Hereby Certify the Above Statement</Text>
                  </Checkbox>
                </Form.Item>
                <Form.Item
                  name="submitted_by"
                  label={<Text strong>Submitted by</Text>}
                  rules={[
                    {
                      required: true,
                      message: "Please input name!",
                    },
                  ]}
                >
                  <Input
                    disabled={!vendorDetails?.is_approver}
                    style={{ maxWidth: 400 }}
                    placeholder="Enter your full name"
                  />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    disabled={!vendorDetails?.is_approver}
                    block
                    style={{ height: '48px', fontSize: '16px' }}
                  >
                    Submit PQQ
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </div>
        );
  }

  function recertifyGateBlock() {
    if (!isRecertify || isRecertifyAttested) return null;

    let currentStep = 0;
    if (hasReviewedProfile) currentStep = 1;
    if (isRecertifyAttested) currentStep = 2;

    const goToProfile = () => {
      markProfileReviewed();
      history.push(`/vendor/${vendorId}/vendor-profile/basic-details`);
    };

    return (
      <Card
        title={
          <Space>
            <CheckCircleOutlined style={{ color: "#fa8c16" }} />
            <Text strong>Recertification Required</Text>
            <Tag color="orange">Annual</Tag>
          </Space>
        }
        style={{ marginBottom: "24px", border: "2px solid #fa8c16" }}
      >
        <Alert
          message="Please confirm your information before paying"
          description="Recertification renews your i4Profile for another year. Review every section, update anything that has changed, and confirm your information is current. Payment is enabled once you confirm."
          type="warning"
          showIcon
          style={{ marginBottom: "20px" }}
        />
        <Steps direction="vertical" current={currentStep} size="small">
          <Steps.Step
            title={<Text strong>Review your i4Profile</Text>}
            description={
              <div style={{ marginTop: 8 }}>
                <Paragraph style={{ marginBottom: 8 }}>
                  Open your i4Profile and review every section. Update anything
                  that has changed since your last submission, then return here.
                </Paragraph>
                <Space>
                  <Button type="primary" onClick={goToProfile}>
                    Open i4Profile
                  </Button>
                  {hasReviewedProfile && (
                    <Tag color="green">Marked as reviewed</Tag>
                  )}
                </Space>
              </div>
            }
          />
          <Steps.Step
            title={<Text strong>Confirm information is current</Text>}
            description={
              <div style={{ marginTop: 8 }}>
                <Paragraph style={{ marginBottom: 8 }}>
                  Certify that the information in your i4Profile has been
                  reviewed and is accurate as of today.
                </Paragraph>
                <Button
                  type="primary"
                  onClick={() => setAttestModalOpen(true)}
                  disabled={!hasReviewedProfile}
                >
                  Confirm Information is Current
                </Button>
                {!hasReviewedProfile && (
                  <Text
                    type="secondary"
                    style={{ marginLeft: 12, fontSize: 12 }}
                  >
                    Complete step 1 first
                  </Text>
                )}
              </div>
            }
          />
          <Steps.Step
            title={<Text strong>Reattach and pay</Text>}
            description={
              <Text type="secondary">
                Once confirmed above, you'll be able to reattach your i4Profile
                and pay $750.00 + 2.9% transaction fee for this cycle.
              </Text>
            }
          />
        </Steps>

        <Modal
          title="Confirm i4Profile is Current"
          open={attestModalOpen}
          onCancel={() => {
            if (attesting) return;
            setAttestModalOpen(false);
            setAttestChecked(false);
          }}
          okText="I Confirm"
          okButtonProps={{ disabled: !attestChecked, loading: attesting }}
          cancelButtonProps={{ disabled: attesting }}
          onOk={submitAttestation}
          destroyOnClose
        >
          <Paragraph>
            By confirming, you certify that you have reviewed every section of
            your i4Profile and that all information is accurate and current as
            of today,{" "}
            <Text strong>{moment().format("MMMM D, YYYY")}</Text>.
          </Paragraph>
          <Checkbox
            checked={attestChecked}
            onChange={(e) => setAttestChecked(e.target.checked)}
          >
            I have reviewed my i4Profile and certify the information is current.
          </Checkbox>
        </Modal>
      </Card>
    );
  }

  function actionSummaryCard() {
    if (application?.is_approved_and_submitted && !isRecertify) {
      return null;
    }

    const needsPayment = isRecertify || !application?.is_paid;
    const needsCertification =
      !needsPayment && !application?.is_approved_and_submitted;

    if (!needsPayment && !needsCertification) {
      return null;
    }

    return (
      <Card
        style={{
          marginBottom: '24px',
          backgroundColor: needsPayment ? '#fff7e6' : '#f6ffed',
          border: needsPayment ? '1px solid #ffd591' : '1px solid #b7eb8f',
        }}
      >
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong style={{ fontSize: '16px' }}>
                {needsPayment ? 'Next Step: Complete Payment' : 'Next Step: Certify and Submit'}
              </Text>
              <Text type="secondary">
                {needsPayment
                  ? 'Payment is required before you can certify and submit your PQQ.'
                  : 'Review your PQQ details below, then scroll down to certify and submit.'}
              </Text>
            </Space>
          </Col>
          <Col>
            <Space>
              {needsPayment ? (
                <Button type="primary" size="large" onClick={handlePayment}>
                  Pay Now
                </Button>
              ) : (
                <Button type="primary" size="large" onClick={scrollToCertification} icon={<ArrowDownOutlined />}>
                  Go to Certification
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </Card>
    );
  }

  function fileViewer(file) {
    return (
      <Text>
        {file?.url ? (
          <a href={file?.url} target="_blank" rel="noreferrer">
            View Report <FilePdfOutlined />
          </a>
        ) : (
          "-"
        )}
      </Text>
    );
  }

  function remarksBlock() {
    return (
      <AlertWizz
        message={
          <List
            itemLayout="horizontal"
            dataSource={application && [application]}
            renderItem={(item) => (
              <List.Item>
                <Row style={{ width: "100%" }} type="flex">
                  <Col sm={8} md={10} lg={12} xl={10}>
                    <p>Status</p>
                    <Tag
                      title="PQQ Status"
                      color={
                        applicationStatusValues.find(
                          (eachStatus) =>
                            eachStatus?.value === item?.status &&
                            eachStatus?.color
                        )?.color
                      }
                    >
                      {
                        applicationStatusValues.find(
                          (eachStatus) => eachStatus?.value === item?.status
                        )?.label
                      }
                    </Tag>
                    {item?.status === "InformationRequired" && (
                      <Text ellipsis>
                        ({item.information_required?.join(", ")})
                      </Text>
                    )}
                  </Col>
                  <Col sm={8} md={7} lg={6} xl={5}>
                    <p>Priority</p>
                    <Tag
                      title="Priority"
                      color={
                        adminApplicationPriorityOptions.find(
                          (eachStatus) =>
                            eachStatus?.value === item?.priority &&
                            eachStatus?.color
                        )?.color
                      }
                    >
                      {
                        adminApplicationPriorityOptions.find(
                          (eachStatus) => eachStatus?.value === item?.priority
                        )?.label
                      }
                    </Tag>
                  </Col>
                  <Col sm={8} md={7} lg={6} xl={5}>
                    <p>Assignee</p>
                    <Tag title="Assignee" color="default">
                      {item.assignee}
                    </Tag>
                  </Col>
                  {!userDetails?.is_preconstruction_role && (
                    <Col sm={8} md={7} lg={6} xl={4}>
                      <p>Report</p>
                      {fileViewer(item.certificate)}
                    </Col>
                  )}
                </Row>
              </List.Item>
            )}
          />
        }
        description={
          <Row style={{ width: "100%" }} type="flex">
            <Col span={24}>Remarks: {application?.remarks}</Col>
          </Row>
        }
        type="success"
        action={
          (!isUserClientOrProjectAdmin() ||
            !userDetails?.is_preconstruction_role) && (
            <Space direction="vertical">
              <Button
                size="small"
                type="ghost"
                onClick={() => setShowDrawer(true)}
              >
                Edit
              </Button>
            </Space>
          )
        }
      />
    );
  }

  function PQQDescriptionBlock() {
    return (
      <Collapse defaultActiveKey={["1"]} ghost>
        <Panel header="i4Profile" key="1">
          <PQQDescriptions data={data && data} />
        </Panel>
      </Collapse>
    );
  }
  const showRecertifyGate =
    !isUserAdmin && isRecertify && !isRecertifyAttested;

  return (
    <Row>
      <Col span={20} offset={2}>
        {isLoading ? (
          <Skeleton active />
        ) : showRecertifyGate ? (
          <Fragment>
            {recertifyGateBlock()}
            {data && PQQDescriptionBlock()}
          </Fragment>
        ) : data ? (
          isUserAdmin ? (
            <>
              {remarksBlock()}
              {PQQDescriptionBlock()}
            </>
          ) : (
            <Fragment>
              <Row>
                <Col span={18} offset={3}>
                  <ResultWizz {...appStatusProps()} />
                </Col>
              </Row>

              {/* Progress Steps */}
              {!application?.is_approved_and_submitted && (
                <Card style={{ marginBottom: '24px' }}>
                  <Steps className="pqq-progress-steps">
                    {getProgressSteps().map((step, index) => (
                      <Steps.Step
                        key={index}
                        title={step.title}
                        status={step.status}
                        icon={step.icon}
                      />
                    ))}
                  </Steps>
                </Card>
              )}

              {actionSummaryCard()}

              {PQQDescriptionBlock()}
              {discardAndReattach()}
              {certifyBlock()}
            </Fragment>
          )
        ) : (
          <ResultWizz {...resultProps()} />
        )}
      </Col>
    </Row>
  );
};

export default PQQContent;
